import {DefinitionsList as DefinitionsListShared} from '@github-ui/custom-properties-core/components/DefinitionsList'
import {ListEmptyState} from '@github-ui/custom-properties-core/components/ListEmptyState'
import {PropertiesPageTabs} from '@github-ui/custom-properties-core/components/PropertiesPageTabs'
import type {OrgCustomPropertiesListPagePayload} from '@github-ui/custom-properties-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {Pagination, Stack} from '@primer/react'
import {useState} from 'react'

import {getShuffledSuggestions} from '../components/definition-ice-breaker-suggestions'
import {DefinitionIceBreakers} from '../components/DefinitionIceBreakers'
import {DefinitionsPageHeader} from '../components/PropertiesHeaderPageTabs'
import {usePropertiesListResults} from '../hooks/use-properties-list'
import {useEditPropertyPathBuilder} from '../hooks/use-property-details-paths'
import {DefinitionsFilter} from './DefinitionsFilter'

const ICE_BREAKERS_DISPLAY_THRESHOLD = 3

// Shuffled once per JS module evaluation. Computed at module scope so the displayed order is stable across re-renders.
// Note: in SSR, module scope state is shared across requests; DefinitionIceBreakers renders only in the browser to avoid hydration mismatches.
const iceBreakerSuggestions = getShuffledSuggestions()

export function OrgCustomPropertiesListPage() {
  const {permissions, ownDefinitionsCount, ...payload} = useRoutePayload<OrgCustomPropertiesListPagePayload>()
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const showFilterDialog = useFeatureFlag('repos_list_show_filter_dialog')
  const getPropertyDetailsPath = useEditPropertyPathBuilder()

  const [filterQuery, setFilterQuery] = useState('')

  const initialTotalCount = payload.totalCount

  const {
    payload: {totalCount, pageCount, definitions},
    currentPage,
    fetchResults,
  } = usePropertiesListResults({initialPayload: payload})

  const iceBreakerSuggestionsEnabled = isFeatureEnabled('property_definition_empty_state_suggestions')

  const showIceBreakers = initialTotalCount < ICE_BREAKERS_DISPLAY_THRESHOLD && iceBreakerSuggestionsEnabled
  const replaceDefaultEmptyState = initialTotalCount === 0 && iceBreakerSuggestionsEnabled

  return (
    <>
      <DefinitionsPageHeader ownDefinitionsCount={ownDefinitionsCount} permissions={permissions} />
      {permissions === 'all' && (
        <PropertiesPageTabs initialActiveTab="properties" definitionsCount={totalCount} className="tmp-mb-3" />
      )}
      <DefinitionsFilter
        className="tmp-mb-3"
        variant={showFilterDialog ? 'full' : 'input'}
        filterValue={filterQuery}
        onChange={setFilterQuery}
        onSubmit={request => fetchResults({page: 1, filterQuery: request.raw})}
      />

      <Stack>
        <DefinitionsListShared
          totalCount={totalCount}
          definitions={definitions}
          renderItem={definition => (
            <DefinitionsListShared.Item
              key={definition.propertyName}
              settingsLevel="org"
              definition={definition}
              linkTo={getPropertyDetailsPath(definition.propertyName)}
            />
          )}
          // Ice-breakers replace the "No properties have been found" message when the org has no properties.
          // In this case, screen reader users may not have any visual context to know that no properties exist.
          // So we still use the definitions list component because it announces "0 properties found".
          renderEmptyState={replaceDefaultEmptyState ? renderNothing : undefined}
        />
        {showIceBreakers && <IceBreakers />}
      </Stack>

      {pageCount > 1 && (
        <Pagination
          currentPage={currentPage}
          pageCount={pageCount}
          onPageChange={(e, page) => {
            e.preventDefault() // Prevent click event from navigating
            fetchResults({page, filterQuery})
          }}
        />
      )}
    </>
  )
}

function IceBreakers() {
  return (
    <ListEmptyState>
      <DefinitionIceBreakers suggestions={iceBreakerSuggestions} />
    </ListEmptyState>
  )
}

function renderNothing() {
  return null
}
